/**
 * Display formatting for system values — timestamps, byte sizes, counts.
 *
 * Centralised so the whole product agrees. It did not before: the Drive listed
 * times in 24-hour, the sheet's save indicator used the browser default, and
 * two files each had their own byte formatter.
 *
 * Not to be confused with lib/format.ts, which formats *money and business
 * dates*. Those are values in the ledger and carry no timezone; these are
 * moments in time and render in the viewer's zone.
 */

/**
 * The zone every moment here is shown in.
 *
 * The viewer's chosen one, which the setup prompt asks for and the profile
 * page changes — held here rather than threaded through a hundred call sites,
 * and set from the account by AuthContext.
 *
 * It was not held anywhere, and nothing passed a zone to Intl, so every
 * timestamp in the product came out in whatever zone the computer was set to.
 * A Dubai business run from a laptop in Karachi read every upload, invoice and
 * audit line an hour late, and the setting it had been asked for on first run
 * changed nothing at all.
 *
 * Undefined until an account is loaded, which formats in the browser's zone —
 * right for the sign-in screen, where there is no viewer yet.
 */
let viewerZone: string | undefined;

export function setDisplayTimeZone(zone: string | null | undefined): void {
  viewerZone = zone ?? undefined;
}

/** What the formatters pass to Intl. Exported for the clock's own format. */
export function displayTimeZone(): string | undefined {
  return viewerZone;
}

/** `13 Aug 2026, 10:24 PM` */
export function formatDateTime(value: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: viewerZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    // 12-hour with AM/PM throughout, per the client's preference.
    hour12: true,
  }).format(toDate(value));
}

/** `13 Aug 2026` — when the time of day adds nothing. */
export function formatDate(value: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: viewerZone,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(toDate(value));
}

/** `10:24 PM` — for a save indicator, where the date is implied. */
export function formatTime(value: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: viewerZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(toDate(value));
}

/** `10:24:07 PM` — when seconds matter, as in an audit trail. */
export function formatTimeWithSeconds(value: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: viewerZone,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(toDate(value));
}

/** `17 September 2026` — written out, for the body of a letter. */
export function formatLongDate(value: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: viewerZone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(toDate(value));
}

/** `Thu 17 Sept` — the day, for the clock in the header. */
export function formatWeekdayDate(value: string | Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: viewerZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(toDate(value));
}

/** `2 hours ago`, falling back to a date once that stops being useful. */
export function formatRelative(value: string | Date): string {
  const then = toDate(value).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 45) return 'just now';
  if (seconds < 90) return 'a minute ago';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

  return formatDate(value);
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Byte sizes.
 *
 * The input is a string because a file may exceed Number.MAX_SAFE_INTEGER and
 * JSON has no other way to carry that faithfully.
 */
export function formatBytes(value: string | number | bigint): string {
  const bytes = typeof value === 'bigint' ? Number(value) : Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  // One decimal below ten, none above: "1.4 MB" reads better than "1 MB",
  // and "847 MB" better than "847.3 MB".
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unit]}`;
}

/** `1,204` */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

/** `3 files` / `1 file` */
export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatCount(count)} ${count === 1 ? singular : plural}`;
}
